import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UploadState, FileList } from "../../types/quickOrder";

const initialState: UploadState = {
  files: [],
  duplicates: [],
  invalids: [],
  error: null,
};

const uploadFilesSlice = createSlice({
  name: "uploadFiles",
  initialState,
  reducers: {
    addFiles(state, action: PayloadAction<FileList[]>) {
      const newFiles = action.payload;

      const generateKey = (file: FileList) =>
        `${file.fileName}_${file.attachment.size}`;

      const existingKeys = new Set(
        state.files.map((file) => generateKey(file))
      );

      const filteredNewFiles = newFiles.filter(
        (file) => !existingKeys.has(generateKey(file))
      );

      const updatedFiles = [...state.files, ...filteredNewFiles];

      const activeFiles = updatedFiles.filter((file) => file.isActive);

      const error =
        activeFiles.length > 10
          ? "You can upload a maximum of 10 files."
          : null;

      state.files = updatedFiles;
      state.error = error;
      state.duplicates = [];
      state.invalids = [];
    },

    setFiles(state, action: PayloadAction<FileList[]>) {
      state.files = action.payload;
    },

    removeFile(state, action: PayloadAction<FileList>) {
      const updatedFiles = state.files.reduce<FileList[]>((acc, file) => {
        const isMatch =
          file.fileName === action.payload.fileName &&
          file.attachment.size === action.payload.attachment?.size;

        if (isMatch) {
          if (file.orderAttachmentID && file.orderAttachmentID !== 0) {
            acc.push({ ...file, isActive: false });
          }
        } else {
          acc.push(file);
        }

        return acc;
      }, []);

      state.files = updatedFiles;
      state.error = null;
      state.duplicates = [];
      state.invalids = [];
    },

    updateFileClarity(
      state,
      action: PayloadAction<{
        fileName: string;
        size: number;
        imageQuality: "G" | "B" | "N" | "analyzing";
      }>
    ) {
      const { fileName, size, imageQuality } = action.payload;

      state.files = state.files.map((file) => {
        const isMatch =
          file.fileName === fileName && file.attachment.size === size;

        return isMatch ? { ...file, imageQuality } : file;
      });
    },

    resetError(state) {
      state.error = null;
      state.duplicates = [];
      state.invalids = [];
    },

    clearAllFiles(state) {
      state.files = [];
      state.error = null;
      state.duplicates = [];
      state.invalids = [];
    },
  },
});

export const {
  addFiles,
  setFiles,
  removeFile,
  updateFileClarity,
  resetError,
  clearAllFiles,
} = uploadFilesSlice.actions;

export default uploadFilesSlice.reducer;
